/**
 * Qualification Standards for Pro Athletes
 * Times are in seconds.
 * Sources: Olympic Games Los Angeles 2028, World Athletics Championships 2027,
 * USATF Outdoor Championships 2026, US Olympic Trials 2028 A Standards
 */

// Helper: convert "M:SS.ss" or "H:MM:SS" to seconds
function t(str) {
  const parts = str.split(':').map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return parseFloat(str);
}

export const QUAL_STANDARDS = [
  // ── Olympic Games Los Angeles 2028 ───────────────────────────────
  {
    key: 'olympic-la-2028',
    label: 'Olympic Games',
    sublabel: 'Los Angeles 2028',
    tier: 1,
    standards: {
      '800m':          { male: t('1:44.70'), female: t('1:59.50') },
      '1500m':         { male: t('3:33.50'), female: t('4:02.50') },
      'mile':          { male: t('3:50.20'), female: t('4:20.60') },
      '5000m':         { male: t('13:05.00'), female: t('14:52.00') },
      '10000m':        { male: t('27:00.00'), female: t('30:40.00') },
      'half-marathon': { male: t('1:01:00'), female: t('1:08:00') },
      'marathon':      { male: t('2:08:10'), female: t('2:26:50') },
      '5K-road':       { male: t('13:05.00'), female: t('14:52.00') },
      '10K-road':      { male: t('27:00.00'), female: t('30:40.00') },
    },
  },
  // ── World Athletics Championships 2027 ───────────────────────────
  {
    key: 'world-athletics-2027',
    label: 'World Athletics Championships',
    sublabel: '2027',
    tier: 2,
    standards: {
      '800m':          { male: t('1:45.20'), female: t('2:00.00') },
      '1500m':         { male: t('3:35.00'), female: t('4:04.00') },
      'mile':          { male: t('3:52.00'), female: t('4:22.50') },
      '3000m':         { male: t('7:40.00'), female: t('8:45.00') },
      '5000m':         { male: t('13:13.50'), female: t('15:10.00') },
      '10000m':        { male: t('27:28.00'), female: t('31:25.00') },
      'half-marathon': { male: t('1:02:00'), female: t('1:09:30') },
      'marathon':      { male: t('2:09:30'), female: t('2:28:00') },
      '5K-road':       { male: t('13:13.50'), female: t('15:10.00') },
      '10K-road':      { male: t('27:28.00'), female: t('31:25.00') },
    },
  },
  // ── USATF Outdoor Championships 2026 ─────────────────────────────
  {
    key: 'usatf-outdoor-2026',
    label: 'USATF Outdoor Championships',
    sublabel: '2026',
    tier: 3,
    standards: {
      '800m':          { male: t('1:47.00'), female: t('2:02.00') },
      '1500m':         { male: t('3:38.00'), female: t('4:09.00') },
      'mile':          { male: t('3:57.00'), female: t('4:27.00') },
      '3000m':         { male: t('7:55.00'), female: t('9:00.00') },
      '5000m':         { male: t('13:35.00'), female: t('15:30.00') },
      '10000m':        { male: t('28:20.00'), female: t('32:00.00') },
      'half-marathon': { male: t('1:04:00'), female: t('1:13:00') },
      'marathon':      { male: t('2:14:00'), female: t('2:33:00') },
      '5K-road':       { male: t('13:35.00'), female: t('15:30.00') },
      '10K-road':      { male: t('28:20.00'), female: t('32:00.00') },
    },
  },
  // ── US Olympic Trials 2028 A Standards ───────────────────────────
  {
    key: 'us-trials-2028',
    label: 'US Olympic Trials A Standard',
    sublabel: '2028',
    tier: 4,
    standards: {
      '800m':          { male: t('1:46.00'), female: t('2:01.00') },
      '1500m':         { male: t('3:36.00'), female: t('4:05.00') },
      'mile':          { male: t('3:53.50'), female: t('4:24.00') },
      '3000m':         { male: t('7:50.00'), female: t('8:55.00') },
      '5000m':         { male: t('13:25.00'), female: t('15:25.00') },
      '10000m':        { male: t('27:50.00'), female: t('31:45.00') },
      'half-marathon': { male: t('1:03:00'), female: t('1:11:00') },
      'marathon':      { male: t('2:11:00'), female: t('2:29:30') },
      '5K-road':       { male: t('13:25.00'), female: t('15:25.00') },
      '10K-road':      { male: t('27:50.00'), female: t('31:45.00') },
    },
  },
];

/**
 * Format seconds into a readable time string for standards display.
 * e.g. 223.5 → "3:43.50", 3660 → "1:01:00"
 */
export function formatStdTime(totalSeconds) {
  if (!totalSeconds || totalSeconds <= 0) return '—';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sFull = totalSeconds % 60;
  const sInt = Math.floor(sFull);
  const sFrac = (sFull % 1 !== 0) ? '.' + (sFull - sInt).toFixed(2).slice(2) : '';
  const sStr = sInt.toString().padStart(2, '0') + sFrac;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sStr}`;
  return `${m}:${sStr}`;
}

/**
 * Check which qualification standards an athlete has achieved.
 *
 * @param {Array} benchmarks - array of { event, time_seconds, race_date }
 * @param {string} gender - 'male' | 'female'
 * @returns {Array} - array of { standardKey, standardLabel, sublabel, event, time_seconds, race_date, requiredSeconds }
 */
export function checkQualifiers(benchmarks, gender) {
  if (!benchmarks || !benchmarks.length || !gender) return [];
  const results = [];

  for (const std of QUAL_STANDARDS) {
    for (const [event, times] of Object.entries(std.standards)) {
      const required = times[gender];
      if (!required) continue;

      // Find best benchmark for this event
      const matches = benchmarks.filter(b => b.event === event && b.time_seconds > 0);
      if (!matches.length) continue;

      const best = matches.reduce((a, b) => (a.time_seconds < b.time_seconds ? a : b));
      if (best.time_seconds <= required) {
        results.push({
          standardKey: std.key,
          standardLabel: std.label,
          sublabel: std.sublabel,
          tier: std.tier,
          event,
          time_seconds: best.time_seconds,
          race_date: best.race_date,
          requiredSeconds: required,
        });
      }
    }
  }

  // Sort by tier then event
  results.sort((a, b) => a.tier - b.tier || a.event.localeCompare(b.event));
  return results;
}
